import { useCallback } from 'react';

// Extend Window interface to include WT
declare global {
  interface Window {
    WT?: {
      optimizeModule?: {
        prototype?: {
          wtConfigObj?: {
            data?: Record<string, any>;
          };
        };
      };
    };
  }
}

/**
 * Hook for managing WT (Webtrends) optimization module data
 * Provides a clean interface for setting tracking flags
 */
export const useWTODataLayer = () => {
  /**
   * Sets a value in the WT optimization module data object
   * @param key - The key to set in the data object
   * @param value - The value to set (defaults to true)
   */
  const setWTData = useCallback((key: string, value: any = true) => {
    // Ensure WT is available to avoid runtime errors
    if (
      typeof window !== 'undefined' &&
      window.WT &&
      window.WT.optimizeModule &&
      window.WT.optimizeModule.prototype &&
      window.WT.optimizeModule.prototype.wtConfigObj &&
      window.WT.optimizeModule.prototype.wtConfigObj.data
    ) {
      window.WT.optimizeModule.prototype.wtConfigObj.data[key] = value;
    }
  }, []);

  /**
   * Gets a value from the WT optimization module data object
   * @param key - The key to get from the data object
   * @returns The value or undefined if not available
   */
  const getWTData = useCallback((key: string) => {
    if (
      typeof window !== 'undefined' &&
      window.WT &&
      window.WT.optimizeModule &&
      window.WT.optimizeModule.prototype &&
      window.WT.optimizeModule.prototype.wtConfigObj &&
      window.WT.optimizeModule.prototype.wtConfigObj.data
    ) {
      return window.WT.optimizeModule.prototype.wtConfigObj.data[key];
    }
    return undefined;
  }, []);

  /**
   * Checks if the WT optimization module is available
   * @returns boolean indicating if WT is available
   */
  const isWTAvailable = useCallback(
    () =>
      typeof window !== 'undefined' &&
      window.WT &&
      window.WT.optimizeModule &&
      window.WT.optimizeModule.prototype &&
      window.WT.optimizeModule.prototype.wtConfigObj &&
      window.WT.optimizeModule.prototype.wtConfigObj.data,
    []
  );

  /**
   * Removes a specific key from the WT optimization module data object
   * @param key - The key to remove from the data object
   */
  const removeWTData = useCallback((key: string) => {
    if (
      typeof window !== 'undefined' &&
      window.WT &&
      window.WT.optimizeModule &&
      window.WT.optimizeModule.prototype &&
      window.WT.optimizeModule.prototype.wtConfigObj &&
      window.WT.optimizeModule.prototype.wtConfigObj.data
    ) {
      delete window.WT.optimizeModule.prototype.wtConfigObj.data[key];
    }
  }, []);

  /**
   * Clears all data from the WT optimization module data object
   * Use with caution as this removes all tracking data
   */
  const clearAllWTData = useCallback(() => {
    if (
      typeof window !== 'undefined' &&
      window.WT &&
      window.WT.optimizeModule &&
      window.WT.optimizeModule.prototype &&
      window.WT.optimizeModule.prototype.wtConfigObj &&
      window.WT.optimizeModule.prototype.wtConfigObj.data
    ) {
      window.WT.optimizeModule.prototype.wtConfigObj.data = {};
    }
  }, []);

  /**
   * Resets specific tracking flags to false
   * @param keys - Array of keys to reset to false
   */
  const resetWTFlags = useCallback(
    (keys: string[]) => {
      keys.forEach((key) => setWTData(key, false));
    },
    [setWTData]
  );

  return {
    setWTData,
    getWTData,
    isWTAvailable,
    removeWTData,
    clearAllWTData,
    resetWTFlags,
  };
};

export default useWTODataLayer;
